import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  Banknote,
  ChevronRight,
  FileX,
  Mail,
  Percent,
  Receipt,
  SearchX,
  Sparkles,
} from "lucide-react";
import { z } from "zod";
import {
  EXPENSE_CATEGORIES,
  EXPENSE_INVOICE_STATUSES,
  EXPENSE_SORT_FIELDS,
  SORT_DIRECTIONS,
  UNASSIGNED_PROJECT,
  auDate,
  fromYmd,
  isValidYmd,
  type ExpenseInvoiceDetail,
  type ExpenseInvoiceRow,
  type ExpenseSortField,
} from "@contractorhub/shared";
import {
  Alert,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  StatCard,
  Tabs,
  type Column,
} from "../../../components/ui";
import {
  Amount,
  AmountOrZero,
  TaxIncluded,
  audOnly,
  noCurrencyHint,
} from "../../../components/invoices/invoice-display";
import { RecordExpenseModal } from "../../../components/expenses/record-expense-modal";
import { ExpenseBatchModal } from "../../../components/expenses/expense-batch-modal";
import { AssignProjectBar } from "../../../components/expenses/assign-project-bar";
import { TakePhotoButton } from "../../../components/expenses/take-photo-button";
import { MultiPdfDropZone, type PickedPdf } from "../../../components/invoices/multi-pdf-drop-zone";
import { apiUpload } from "../../../lib/api";
import { useUploadBatch } from "../../../lib/use-upload-batch";
import {
  ExpenseStatusBadge,
  ExpenseVerdictBadge,
  categoryLabel,
} from "../../../components/expenses/expense-bits";
import { adminExpensesQuery, adminProjectsQuery, invalidateExpenseViews, queryKeys } from "../../../lib/queries";
import { useRememberList } from "../../../lib/list-return";
import { RangePresets } from "../../../components/range-presets";

const searchSchema = z
  .object({
    status: z.enum([...EXPENSE_INVOICE_STATUSES, "ALL"]).default("ALL"),
    q: z.string().max(120).optional(),
    projectId: z.string().optional(),
    vendor: z.string().optional(),
    category: z.enum(EXPENSE_CATEGORIES).optional(),
    from: z.string().refine(isValidYmd).optional(),
    to: z.string().refine(isValidYmd).optional(),
    sort: z.enum(EXPENSE_SORT_FIELDS).default("expenseDate"),
    dir: z.enum(SORT_DIRECTIONS).default("desc"),
    page: z.coerce.number().int().min(1).default(1),
  })
  .transform((s) =>
    s.from && s.to && s.from > s.to ? { ...s, from: s.to, to: s.from } : s
  );

export const Route = createFileRoute("/_authed/_admin/expenses")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminExpensesQuery(deps)),
      context.queryClient.ensureQueryData(adminProjectsQuery),
    ]),
  component: ExpensesPage,
});

// Recording an expense is paying it (EXP-7): live or void, nothing between.
const TABS = [
  { value: "ALL", label: "All", key: "all" },
  { value: "PAID", label: "Paid", key: "paid" },
  { value: "VOID", label: "Void", key: "void" },
] as const;

/**
 * Supplier invoices and receipts recorded against projects (EXP) — the
 * business's own spend, beside the contractor invoices. Grouped by vendor,
 * filed by project, and checked by the same document checks.
 */
function ExpensesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(adminExpensesQuery(search));
  useRememberList("expenses", search, data.rows.map((row) => row.id));
  const { data: projectData } = useSuspenseQuery(adminProjectsQuery);
  const queryClient = useQueryClient();
  const [recording, setRecording] = useState(false);
  // Ticked rows, for a bulk project assignment. Dropped whenever the query
  // changes: ids from the last page are valid ids the admin no longer sees.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const queryKey = JSON.stringify(search);
  const [lastQueryKey, setLastQueryKey] = useState(queryKey);
  if (queryKey !== lastQueryKey) {
    setLastQueryKey(queryKey);
    if (selectedIds.size > 0) setSelectedIds(new Set());
  }
  const selected = data.rows.filter((row) => selectedIds.has(row.id)).map((row) => row.id);
  // Which review is open: the drop just made, or every upload still waiting.
  const [reviewing, setReviewing] = useState<"batch" | "needsDetails" | null>(null);
  // Uploads with no details said: the page's drop is "read it and tell me"
  // (EXP-8). Each lands as an expense the review then prefills.
  const batch = useUploadBatch<ExpenseInvoiceDetail>({
    upload: (file) =>
      apiUpload("POST /api/manage/expense-uploads", {
        fields: { projectId: "", category: "", expenseDate: "", file },
      }),
    onUploaded: (detail) => {
      queryClient.setQueryData(queryKeys.adminExpenseDetail(detail.id), detail);
      void invalidateExpenseViews(queryClient);
    },
  });

  /** A drop or a photo: each file uploads at once, and the review opens on them. */
  function startBatch(files: PickedPdf[]) {
    // A finished batch is done with: the new drop starts its own.
    if (!batch.busy) batch.clear();
    batch.add(files);
    setReviewing("batch");
  }

  const patch = (next: Partial<typeof search>) =>
    void navigate({ to: "/expenses", search: { ...search, ...next, page: 1 } });
  const open = (row: ExpenseInvoiceRow) =>
    void navigate({ to: "/expenses/$id", params: { id: row.id } });
  const filtered = Boolean(
    search.q ||
    search.projectId ||
    search.vendor ||
    search.category ||
    search.from ||
    search.to
  );

  const columns: Column<ExpenseInvoiceRow>[] = [
    {
      key: "expenseDate",
      header: "Date",
      sortable: true,
      cell: (row) => (
        <span className="whitespace-nowrap tabular-nums text-text">
          {auDate(fromYmd(row.expenseDate))}
        </span>
      ),
    },
    {
      key: "supplier",
      header: "Vendor",
      sortable: true,
      cell: (row) => (
        <div className="min-w-0">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              open(row);
            }}
            className="block max-w-[16rem] truncate rounded-sm text-left font-medium text-text hover:text-accent"
          >
            {row.supplierName ?? (
              <span className="font-normal text-subtle">Vendor not read yet</span>
            )}
          </button>
          <div className="truncate text-xs text-subtle">
            {row.source === "EMAIL" && (
              <>
                <span className="inline-flex items-center gap-1 align-[-2px]" title="Emailed to the expenses mailbox">
                  <Mail className="h-3 w-3" aria-hidden="true" />
                  Emailed
                </span>
                {" · "}
              </>
            )}
            {row.invoiceNumber ?? "No number"}
            {row.description ? ` · ${row.description}` : ""}
          </div>
        </div>
      ),
    },
    {
      key: "project",
      header: "Project",
      priority: "md",
      cell: (row) =>
        row.projectName ? (
          <div className="min-w-0">
            <div className="truncate text-text">{row.projectName}</div>
            <div className="truncate text-xs text-subtle">{row.clientName}</div>
          </div>
        ) : (
          <span className="text-xs text-subtle">No project</span>
        ),
    },
    {
      key: "category",
      header: "Category",
      priority: "lg",
      cell: (row) => <span className="text-muted">{categoryLabel(row.category)}</span>,
    },
    {
      key: "total",
      header: "Amount",
      align: "right",
      sortable: true,
      cell: (row) => (
        <div className="whitespace-nowrap">
          <Amount
            value={row.total}
            currency={row.currency}
            className="font-semibold text-text"
          />
          <TaxIncluded
            taxAmount={row.taxAmount}
            total={row.total}
            currency={row.currency}
            assumed={row.assumedFields.includes("taxAmount")}
          />
        </div>
      ),
    },
    {
      // What is worth a look, and nothing else. Every live expense is paid
      // (EXP-7), so a "Paid" badge on each row said nothing, and a clean row now
      // stays quiet — only void, details to confirm, or a verdict that flags
      // something (EXP-10) shows here.
      key: "status",
      header: "Checks",
      cell: (row) =>
        row.status === "VOID" ? (
          <ExpenseStatusBadge status={row.status} />
        ) : (
          <div className="flex flex-col items-start gap-1">
            {!row.detailsConfirmed && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-accent-on-soft">
                <Sparkles className="h-3 w-3" aria-hidden="true" /> Needs details
              </span>
            )}
            {row.validationStatus !== "MATCHED" && (
              <ExpenseVerdictBadge status={row.validationStatus} extractionStatus={row.extractionStatus} />
            )}
          </div>
        ),
    },
    {
      key: "open",
      header: <span className="sr-only">Open</span>,
      align: "right",
      width: "w-10",
      cell: (row) => (
        <button
          type="button"
          aria-label={`Open ${row.supplierName ?? "expense"}, ${row.expenseDate}`}
          onClick={(event) => {
            event.stopPropagation();
            open(row);
          }}
          className="grid h-8 w-8 place-items-center rounded-md text-subtle transition-colors hover:bg-surface-hover hover:text-text"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Expenses"
        description="Supplier invoices and receipts, recorded against the projects they were for."
      />
      <div className="mb-6 space-y-3">
        {/* On a phone or tablet, the camera leads; the drop zone stays for files. */}
        <TakePhotoButton className="w-full sm:w-auto" onPhotos={startBatch} />
        <MultiPdfDropZone
          kind="expense"
          label="Expense documents"
          title="Drop expense invoices, receipts or photos of them here, or"
          hint="PDFs or images, several at once — each is read, and its project, category and date are filled in for you to check."
          onFiles={startBatch}
          footer={
            <>
              No document?{" "}
              <Button variant="link" size="sm" onClick={() => setRecording(true)}>
                Enter one by hand
              </Button>
              {/* Only where a mailbox is read (EXP-11): the address is the API's to say. */}
              {data.mailbox && (
                <span className="mt-1 block sm:mt-0 sm:inline">
                  <span className="hidden sm:inline"> · </span>Or email them to{" "}
                  <a href={`mailto:${data.mailbox}`} className="text-accent hover:underline underline-offset-2">
                    {data.mailbox}
                  </a>
                </span>
              )}
            </>
          }
        />
        {data.counts.needsDetails > 0 && reviewing === null && (
          <Alert
            tone="info"
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  // Still uploading: back to that batch. Otherwise every upload
                  // waiting for details, not just this drop's.
                  if (batch.busy) return setReviewing("batch");
                  batch.clear();
                  setReviewing("needsDetails");
                }}
              >
                Review
              </Button>
            }
          >
            {data.counts.needsDetails === 1
              ? "1 uploaded expense needs its project, category and date confirmed."
              : `${data.counts.needsDetails} uploaded expenses need their project, category and date confirmed.`}
          </Alert>
        )}
      </div>
      {recording && (
        <RecordExpenseModal
          open
          initialMode="manual"
          onClose={() => setRecording(false)}
          defaultProjectId={
            search.projectId && search.projectId !== UNASSIGNED_PROJECT
              ? search.projectId
              : null
          }
        />
      )}
      {reviewing === "batch" && (
        <ExpenseBatchModal
          source={{
            kind: "batch",
            entries: batch.items.map((item) => ({
              key: item.key,
              fileName: item.file.name,
              state: item.state,
              error: item.error,
              expenseId: item.result?.id ?? null,
            })),
            onRetry: batch.retry,
            onForget: batch.remove,
            // Snapping receipts one after another: each joins this batch.
            onAdd: batch.add,
          }}
          onClose={() => {
            setReviewing(null);
            // Uploads still on their way keep going; the finished batch is done with.
            if (!batch.busy) batch.clear();
          }}
        />
      )}
      {reviewing === "needsDetails" && (
        <ExpenseBatchModal
          source={{ kind: "needsDetails" }}
          onClose={() => setReviewing(null)}
        />
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Spent"
          value={<AmountOrZero value={data.summary.spent} />}
          hint={withNoCurrency(
            `${data.counts.paid} ${data.counts.paid === 1 ? "expense" : "expenses"}`,
            data.summary.noCurrency
          )}
          icon={Banknote}
          tone="success"
        />
        <StatCard
          label="GST on these"
          value={<AmountOrZero value={audOnly(data.summary.tax)} />}
          hint="AUD expenses, not counting void"
          icon={Percent}
        />
        <StatCard
          label="Void"
          value={data.counts.void}
          hint="Kept on record, out of every total"
          icon={FileX}
        />
      </div>

      <Card className="mb-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Search">
            <SearchInput
              value={search.q ?? ""}
              placeholder="Vendor, number or description"
              onChange={(q) => patch({ q: q || undefined })}
            />
          </Field>
          <Field label="Project">
            <Select
              value={search.projectId ?? ""}
              onChange={(e) => patch({ projectId: e.target.value || undefined })}
            >
              <option value="">Any project</option>
              <option value={UNASSIGNED_PROJECT}>No project</option>
              {projectData.projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.clientName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Vendor" hint="Spellings of one vendor are grouped.">
            <Select
              value={search.vendor ?? ""}
              onChange={(e) => patch({ vendor: e.target.value || undefined })}
            >
              <option value="">Any vendor</option>
              {data.vendors.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.name} ({v.count})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Category">
            <Select
              value={search.category ?? ""}
              onChange={(e) =>
                patch({
                  category: (e.target.value || undefined) as typeof search.category,
                })
              }
            >
              <option value="">Any category</option>
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {categoryLabel(c)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="From">
            <Input
              type="date"
              value={search.from ?? ""}
              onChange={(e) => patch({ from: e.target.value || undefined })}
            />
          </Field>
          <Field label="To">
            <Input
              type="date"
              value={search.to ?? ""}
              onChange={(e) => patch({ to: e.target.value || undefined })}
            />
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <RangePresets value={search} onChange={patch} />
          {filtered && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void navigate({
                  to: "/expenses",
                  search: {
                    status: search.status,
                    sort: search.sort,
                    dir: search.dir,
                    page: 1,
                  },
                })
              }
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <Tabs
          className="px-4"
          value={search.status}
          onChange={(status) => patch({ status })}
          items={TABS.map((t) => ({
            value: t.value,
            label: t.label,
            count: data.counts[t.key],
          }))}
        />
        <DataTable
          caption="Expenses"
          columns={columns}
          rows={data.rows}
          getRowKey={(row) => row.id}
          onRowClick={open}
          selection={{
            selected: selectedIds,
            isSelectable: (row) => row.status !== "VOID",
            rowLabel: (row) => `${row.supplierName ?? "Unread"}, ${auDate(fromYmd(row.expenseDate))}`,
            onToggle: (id) =>
              setSelectedIds((current) => {
                const next = new Set(current);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              }),
            onToggleAll: (keys) =>
              setSelectedIds((current) =>
                keys.length > 0 && keys.every((key) => current.has(key)) ? new Set() : new Set(keys)
              ),
          }}
          sort={{ key: search.sort, direction: search.dir }}
          onSortChange={(next) => {
            const sort = next.key as ExpenseSortField;
            patch({
              sort,
              dir: search.sort === sort && search.dir === "desc" ? "asc" : "desc",
            });
          }}
          empty={
            filtered ? (
              <EmptyState
                icon={SearchX}
                title="No expenses match"
                description="Try clearing a filter."
              />
            ) : (
              <EmptyState
                icon={Receipt}
                title="No expenses yet"
                description="Record a supplier invoice or receipt to start."
              />
            )
          }
        />
        <Pagination
          page={data.pageInfo.page}
          pageSize={data.pageInfo.pageSize}
          total={data.pageInfo.total}
          onPageChange={(page) =>
            void navigate({ to: "/expenses", search: { ...search, page } })
          }
        />
      </Card>
      <AssignProjectBar ids={selected} onClear={() => setSelectedIds(new Set())} />
    </div>
  );
}

function withNoCurrency(hint: string, n: number): string {
  const extra = noCurrencyHint(n);
  return extra ? `${hint} · ${extra}` : hint;
}
